import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import ModalProfileHeader from "../../Common/ModalProfileHeader";
import { capitalizeFirstLetter } from "../../../helpers";

const TransferFundToWallet = ({
    transferFundMutate,
    transferFundLoading,
    handleClose,
    selectedData,
    walletBalance
}) => {
    const initialValues = {
        amount: "",
        remarks: "",
    };

    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const currentBalance = Number(
        selectedData?.balance ??
        selectedData?.walletBalance ??
        selectedData?.availableBalance ??
        0
    );

    const validationSchema = Yup.object({
        amount: Yup.number()
            .required("Amount is required")
            .typeError("Enter a valid amount")
            .min(1, "Amount must be greater than 0")
            .max(
                Number(walletBalance || 0),
                `Amount cannot be greater than your wallet balance of ₹${Number(
                    walletBalance || 0
                ).toLocaleString("en-IN")}`
            ),

        remarks: Yup.string()
            .max(250, "Remarks cannot exceed 250 characters"),
    });

    const handleSubmit = async (values) => {
        const amount = Number(values.amount);

        const payload = {
            staffId: selectedData?.staffId,
            amount,
            remarks: values.remarks?.trim() || "",
        };

        transferFundMutate(payload);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Transfer Funds to Wallet
                </h1>

                <button type="button" onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ModalProfileHeader
                title="Beneficiary Details"
                name={capitalizeFirstLetter(selectedData?.name)}
                subtitle={`#${selectedData?.mobile}`}
                amountLabel={"Current Balance"}
                amount={selectedData?.balance}
                // image={selectedData?.profilePicture}
                onClose={handleClose}
            />

            <div className="custom-scrollbar px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({
                        errors,
                        touched,
                        isSubmitting,
                        values,
                        setFieldValue,
                    }) => {
                        const transferAmount = Number(values.amount || 0);

                        const balanceAfterTransfer =
                            currentBalance + transferAmount;

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* Transfer Amount */}
                                        <InputGroup>
                                            <Label htmlFor="mobileNo">Transfer Amount *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="tel"
                                                    name="amount"
                                                    id="amount"
                                                    autoComplete="off"
                                                    maxLength="10"
                                                    placeholder="Enter wallet balance"
                                                    className={errors.amount && touched.amount ? "border-red-500" : ""}
                                                />
                                            </FieldWrappers>
                                            {errors.amount && touched.amount && (
                                                <ErrorText>{errors.amount}</ErrorText>
                                            )}
                                            <div className="flex justify-between mt-1">
                                                <span className="text-xs text-gray-400">
                                                    Available wallet balance
                                                </span>

                                                <span className="text-xs font-medium text-gray-600">
                                                    ₹{Number(walletBalance || 0).toLocaleString("en-IN")}
                                                </span>
                                            </div>
                                        </InputGroup>

                                        {/* Balance After Transfer */}
                                        {transferAmount > 0 && (
                                            <InputGroup>
                                                <Label>
                                                    Wallet Balance After Transfer
                                                </Label>

                                                <InfoBox>
                                                    <span className="text-lg font-semibold text-green-600">
                                                        ₹
                                                        {balanceAfterTransfer.toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </span>
                                                </InfoBox>
                                            </InputGroup>
                                        )}


                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </SideCancelBtn>

                                    <SubmitBtn
                                        type="submit"
                                        disabled={
                                            isSubmitting ||
                                            transferFundLoading
                                        }
                                    >
                                        {isSubmitting || transferFundLoading
                                            ? "Transferring..."
                                            : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`
    bg-white
    w-full
    h-full
    flex
    flex-col
    relative
`;

const InfoBox = tw.div`
    w-full
    px-3
    py-2.5
    rounded-md
    bg-gray-50
    border
    border-gray-100
`;

const ErrorText = tw.div`
    text-red-500
    text-sm
    ml-0.5
    mt-1
`;

export default TransferFundToWallet;